import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Info } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import { DirectEmailCard, PartnershipCard, EmailLink } from "../components/ContactInfo";
import { submitContact, formatApiError } from "../lib/api";
import { CONTACT_EMAIL } from "../lib/site";

const CATEGORIES = [
  "General inquiry",
  "Product suggestion",
  "Partnership inquiry",
  "Content correction",
  "Advertising inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    category: CATEGORIES[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (![form.name, form.email, form.subject, form.message].every((v) => v.trim())) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      const res = await submitContact(form);
      const delivered = res.email_status === "sent";
      setStatus(delivered ? "sent" : "saved");
      if (delivered) toast.success(`Thanks — your message has been sent to ${CONTACT_EMAIL}.`);
      else toast.success("Thanks — your message has been received.");
      setForm({ name: "", email: "", subject: "", category: CATEGORIES[0], message: "" });
    } catch (err) {
      toast.error(err.response?.status === 429 ? formatApiError(err) : "Could not submit your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14" data-testid="contact-page">
      <Breadcrumbs items={[{ label: "Contact" }]} />
      <header className="mb-12 max-w-2xl">
        <p className="eyebrow mb-3">Contact</p>
        <h1 className="font-playfair text-4xl sm:text-5xl text-neutral-900 tracking-tight leading-[1.05] mb-5">
          Get in touch.
        </h1>
        <p className="text-lg text-neutral-600 leading-relaxed">
          Have a product suggestion, correction, or partnership idea? Email us at{" "}
          <EmailLink testid="contact-intro-email" /> or use the form below.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      <div className="lg:col-span-8 min-w-0">
      {status && (
        <div
          className={`mb-5 flex items-start gap-3 rounded-xl border px-5 py-4 text-sm leading-relaxed ${
            status === "sent" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-indigo-50 border-indigo-100 text-neutral-800"
          }`}
          data-testid="contact-status"
          role="status"
        >
          {status === "sent" ? <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> : <Info size={18} className="shrink-0 mt-0.5 text-indigo-800" />}
          {status === "sent" ? (
            <span>Your message was delivered to <strong>{CONTACT_EMAIL}</strong>. We'll reply to the email address you provided.</span>
          ) : (
            <span>Your message was saved, but email delivery is temporarily unavailable. For a faster reply, email us directly at <EmailLink testid="contact-status-email" />.</span>
          )}
        </div>
      )}
      <form
        onSubmit={onSubmit}
        className="bg-surface border border-neutral-200 rounded-xl p-7 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
        data-testid="contact-form"
      >
        <div>
          <label className="block eyebrow mb-2" htmlFor="c-name">Name</label>
          <input
            id="c-name"
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            data-testid="contact-name"
            className="w-full px-4 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div>
          <label className="block eyebrow mb-2" htmlFor="c-email">Email</label>
          <input
            id="c-email"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            data-testid="contact-email"
            className="w-full px-4 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block eyebrow mb-2" htmlFor="c-subject">Subject</label>
          <input
            id="c-subject"
            type="text"
            required
            value={form.subject}
            onChange={update("subject")}
            data-testid="contact-subject"
            className="w-full px-4 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block eyebrow mb-2" htmlFor="c-category">Category</label>
          <select
            id="c-category"
            value={form.category}
            onChange={update("category")}
            data-testid="contact-category"
            className="w-full px-4 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block eyebrow mb-2" htmlFor="c-message">Message</label>
          <textarea
            id="c-message"
            required
            rows={6}
            value={form.message}
            onChange={update("message")}
            data-testid="contact-message"
            className="w-full px-4 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
          />
        </div>
        <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-neutral-500">
            Messages are delivered to {CONTACT_EMAIL}. We aim to respond within a few business days.
          </p>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="self-start sm:self-auto whitespace-nowrap px-6 py-2.5 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 disabled:opacity-60 transition-colors"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
      </div>
      <aside className="lg:col-span-4 min-w-0 space-y-6">
        <DirectEmailCard />
        <PartnershipCard />
      </aside>
      </div>
    </div>
  );
}
